import { v4 as uuidv4 } from "uuid"
import { XError } from "@/utils/error.utils"
import type { Assertion, AssertionValue } from "@/server/services/playground"

export interface DraftArgRow {
    id: string
    argKey: string
    isRef: boolean
    literalType: "string" | "number" | "boolean"
    literalValue: string
    fromTool: string
    path: string
}

export interface DraftAssertion {
    id: string
    tool: string
    argRows: DraftArgRow[]
}

const isAssertionValueRef = (
    value: AssertionValue
): value is { fromTool: string; path: string } =>
    typeof value === "object" && value !== null

const toDraftArgRow = (argKey: string, value: AssertionValue): DraftArgRow => {
    if (isAssertionValueRef(value)) {
        return {
            id: uuidv4(),
            argKey,
            isRef: true,
            literalType: "string",
            literalValue: "",
            fromTool: value.fromTool,
            path: value.path,
        }
    }

    const literalType: DraftArgRow["literalType"] =
        typeof value === "number"
            ? "number"
            : typeof value === "boolean"
              ? "boolean"
              : "string"

    return {
        id: uuidv4(),
        argKey,
        isRef: false,
        literalType,
        literalValue: String(value),
        fromTool: "",
        path: "",
    }
}

export const toDraftAssertions = (assertions: Assertion[]): DraftAssertion[] =>
    assertions.map(assertion => ({
        id: uuidv4(),
        tool: assertion.tool,
        argRows: Object.entries(assertion.argsMatch).map(([argKey, value]) =>
            toDraftArgRow(argKey, value)
        ),
    }))

const coerceLiteral = (row: DraftArgRow): string | number | boolean => {
    if (row.literalType === "number") return Number(row.literalValue)
    if (row.literalType === "boolean") return row.literalValue === "true"
    return row.literalValue
}

const toAssertionValue = (row: DraftArgRow): AssertionValue =>
    row.isRef
        ? { fromTool: row.fromTool.trim(), path: row.path.trim() }
        : coerceLiteral(row)

const UNSAFE_OBJECT_KEYS = new Set(["__proto__", "constructor", "prototype"])

export const duplicateArgKeysOf = (argRows: DraftArgRow[]): Set<string> => {
    const seen = new Set<string>()
    const duplicates = new Set<string>()
    for (const row of argRows) {
        const key = row.argKey.trim()
        if (key.length === 0) continue
        if (seen.has(key)) duplicates.add(key)
        seen.add(key)
    }
    return duplicates
}

export const hasDuplicateArgKeys = (
    draftAssertions: DraftAssertion[]
): boolean =>
    draftAssertions.some(draft => duplicateArgKeysOf(draft.argRows).size > 0)

export const isUnparsableNumberValue = (value: string): boolean =>
    value.trim().length === 0 || !Number.isFinite(Number(value))

export const toAssertions = (
    draftAssertions: DraftAssertion[]
): Assertion[] => {
    if (hasDuplicateArgKeys(draftAssertions)) {
        throw new XError({
            message:
                "toAssertions received duplicate argument keys; the caller must reject them before calling toAssertions",
        })
    }

    return draftAssertions
        .filter(draft => draft.tool.trim().length > 0)
        .map(draft => ({
            tool: draft.tool.trim(),
            argsMatch: draft.argRows
                .filter(row => {
                    const key = row.argKey.trim()
                    return key.length > 0 && !UNSAFE_OBJECT_KEYS.has(key)
                })
                .reduce<Record<string, AssertionValue>>(
                    (argsMatch, row) => {
                        argsMatch[row.argKey.trim()] = toAssertionValue(row)
                        return argsMatch
                    },
                    Object.create(null) as Record<string, AssertionValue>
                ),
        }))
}
